/**
 * PostHog Analytics Provider for RC Studios
 * 
 * Tracks user interactions without collecting PII (names, emails, message content).
 * Events follow the naming convention: snake_case with descriptive prefixes.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import posthog from 'posthog-js';

// PostHog configuration
const POSTHOG_KEY = 'phc_od3CY9BGQRaz2LWevTP7jo2yVmKEUXcgc5jQxYabPi3P';
const POSTHOG_HOST = 'https://us.i.posthog.com';

// Initialize PostHog
let initialized = false;

export const initPostHog = () => {
  if (initialized || typeof window === 'undefined') return;
  
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Privacy settings - don't capture PII
    autocapture: true,
    capture_pageview: false, // We'll handle this manually for SPA
    capture_pageleave: true,
    disable_session_recording: false,
    persistence: 'localStorage',
    // Mask sensitive data
    mask_all_text: false,
    mask_all_element_attributes: false,
  });
  
  initialized = true;
};

// Page view tracking component
export const PostHogPageView = () => {
  const location = useLocation();

  useEffect(() => {
    if (!initialized) initPostHog();
    
    // Track page view with path info (no PII)
    posthog.capture('$pageview', {
      path: location.pathname,
      search: location.search,
      referrer: document.referrer,
    });
  }, [location.pathname, location.search]);

  return null;
};

// ═══════════════════════════════════════════════════════════════════════════
// ANALYTICS EVENT HELPERS
// All events avoid PII - no names, emails, or message content
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Track AI Systems page interactions
 */
export const trackAIServicePageViewed = (section?: string) => {
  posthog.capture('ai_service_page_viewed', {
    section: section || 'hero',
    path: window.location.pathname,
  });
};

export const trackKasPawsCaseStudyOpened = () => {
  posthog.capture('kaspaws_case_study_opened', {
    source: window.location.pathname,
  });
};

export const trackAIProjectContactClicked = (source: string) => {
  posthog.capture('ai_project_contact_clicked', {
    source,
    path: window.location.pathname,
  });
};

/**
 * Track contact form interactions (no message content)
 */
export const trackContactFormStarted = (service?: string) => {
  posthog.capture('contact_form_started', {
    selected_service: service || 'none',
    path: window.location.pathname,
  });
};

export const trackContactFormCompleted = (service: string) => {
  posthog.capture('contact_form_completed', {
    selected_service: service,
    path: window.location.pathname,
  });
};

/**
 * Track service page views
 */
export const trackServicePageViewed = (serviceName: string) => {
  posthog.capture('service_page_viewed', {
    service: serviceName,
    path: window.location.pathname,
  });
};

/**
 * Track pricing interactions
 */
export const trackPricingPackageViewed = (category: string, packageName: string) => {
  posthog.capture('pricing_package_viewed', {
    category,
    package_name: packageName,
  });
};

export const trackPricingCTAClicked = (category: string, packageName: string, ctaType: string) => {
  posthog.capture('pricing_cta_clicked', {
    category,
    package_name: packageName,
    cta_type: ctaType, // 'book_now', 'order_now', 'contact'
  });
};

/**
 * Track order flow
 */
export const trackOrderStarted = (serviceType: string) => {
  posthog.capture('order_started', {
    service_type: serviceType,
  });
};

export const trackOrderCompleted = (serviceType: string) => {
  posthog.capture('order_completed', {
    service_type: serviceType,
  });
};

/**
 * Track navigation interactions
 */
export const trackNavMenuOpened = (menuName: string) => {
  posthog.capture('nav_menu_opened', {
    menu: menuName,
  });
};

export const trackNavItemClicked = (menuName: string, itemName: string) => {
  posthog.capture('nav_item_clicked', {
    menu: menuName,
    item: itemName,
  });
};

/**
 * Track CTA button clicks
 */
export const trackCTAClicked = (ctaName: string, location: string) => {
  posthog.capture('cta_clicked', {
    cta_name: ctaName,
    location,
    path: window.location.pathname,
  });
};

/**
 * Identify user (call after login - only with user ID, no email)
 */
export const identifyUser = (userId: string, isAdmin: boolean = false) => {
  posthog.identify(userId, {
    is_admin: isAdmin,
  });
};

/**
 * Reset user (call on logout)
 */
export const resetUser = () => {
  posthog.reset();
};

export default posthog;
